import { Fragment, ReactNode, useState } from 'react'
import Collapsible from 'react-collapsible'
import { useTranslation } from 'react-i18next'

import Button from '@/modules/shared/components/button/Button'
import { ThemeWrapper } from '@/modules/shared/components/theme-wrapper/ThemeWrapper'
import { Tooltip } from '@/modules/shared/components/tooltip/Tooltip'
import { useWindowSize } from '@/modules/shared/hooks/useWindowSize'
import { ChevronDownIcon } from '@/modules/shared/icons/ChevronDownIcon'
import { Themes } from '@/modules/shared/types'
import { classNames } from '@/modules/shared/utils/classNames'

export interface PageHeaderAction {
  title?: string
  onClick?: () => void
  testId?: string
  tooltip?: ReactNode
  icon?: (props: React.ComponentProps<'svg'>) => React.JSX.Element
  show?: boolean
  render?: () => ReactNode
  loading?: boolean
  disabled?: boolean
}

export interface PageHeaderProps {
  title: ReactNode
  subTitle: string | ReactNode
  icon: (props: React.ComponentProps<'svg'>) => React.JSX.Element
  actions?: PageHeaderAction[]
  testId?: string
}

export function PageHeader({ actions = [], icon: Icon, title, subTitle, testId }: PageHeaderProps) {
  const { t } = useTranslation()
  const [buttonIsOpen, setButtonIsOpen] = useState(false)
  const { sm } = useWindowSize()
  return (
    <section
      className="flex w-full flex-col justify-between rounded-md bg-white shadow sm:flex-row"
      data-testid={testId || 'page-header-wrapper'}
    >
      <div className="flex items-center gap-4 p-4">
        <ThemeWrapper theme={Themes.Primary} className="flex size-14 items-center justify-center rounded-md">
          <Icon className="size-10" />
        </ThemeWrapper>
        <div>
          <p className="font-bold" data-testid="page-header-title">
            {title}
          </p>
          <p className="text-sm text-gray-500" data-testid="page-header-subtitle">
            {subTitle}
          </p>
        </div>
      </div>
      {actions.length > 0 && (
        <>
          <hr className="sm:hidden" />
          <div className="hidden items-center justify-between px-4 sm:flex">
            <div className="flex items-center justify-center gap-x-2">
              {actions.map(({ icon, show = true, testId, tooltip, render, disabled, loading, onClick }, idx) => {
                const Icon = icon
                if (!show) return null
                if (render) return <Fragment key={idx}>{render()}</Fragment>
                return (
                  <Tooltip key={idx} content={tooltip || ''} showArrow={false}>
                    <Button
                      data-testid={testId}
                      disabled={disabled}
                      loading={loading}
                      className="flex size-11 items-center justify-center rounded-full bg-gray-200"
                      onClick={() => onClick && onClick()}
                      type="button"
                    >
                      {Icon && <Icon className="size-8 text-gray-600" />}
                    </Button>
                  </Tooltip>
                )
              })}
            </div>
          </div>
          {!sm && (
            <div className="sm:hidden">
              <Collapsible
                triggerTagName="div"
                easing="ease-in-out"
                transitionTime={200}
                transitionCloseTime={0}
                onOpening={() => setButtonIsOpen(true)}
                onClosing={() => setButtonIsOpen(false)}
                trigger={
                  <Button
                    className={classNames('flex w-full items-center justify-between p-3', { 'border-b': buttonIsOpen })}
                  >
                    <p className="text-sm">{t('general.availableActions', 'Available Actions')}</p>
                    <div className="flex size-11 items-center justify-center rounded-full bg-gray-200">
                      <ChevronDownIcon
                        className={classNames('size-8 text-gray-500 transition-all duration-300', {
                          '-rotate-180 transform': buttonIsOpen,
                        })}
                      />
                    </div>
                  </Button>
                }
              >
                <div className="py-2">
                  {actions.map(
                    ({ icon, show = true, testId, title, tooltip, render, disabled, loading, onClick }, idx) => {
                      const Icon = icon
                      if (!show) return null
                      return (
                        <div
                          className="flex w-full items-center justify-between px-3 py-2"
                          data-testid={testId}
                          key={idx}
                        >
                          <p className="text-sm">{title || tooltip}</p>
                          {render && render()}
                          {!render && (
                            <Button
                              disabled={disabled}
                              loading={loading}
                              className="flex size-11 items-center justify-center rounded-full bg-gray-200"
                              onClick={() => onClick && onClick()}
                            >
                              {Icon && <Icon className="size-8 text-gray-600" />}
                            </Button>
                          )}
                        </div>
                      )
                    }
                  )}
                </div>
              </Collapsible>
            </div>
          )}
        </>
      )}
    </section>
  )
}
